import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Quote-cart context. Persists per-user in MongoDB via /api/cart.
 *
 * Loaded on login, refreshed on demand. All mutations re-fetch the full cart
 * so the client + server stay in lock-step (cart payloads are small).
 */
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setCart({ items: [] });
      return;
    }
    try {
      setLoading(true);
      const res = await api.get("/cart");
      setCart(res.data || { items: [] });
    } catch {
      setCart({ items: [] });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch when user changes (login/logout)
  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = useCallback(
    async (product_id, { quantity = 1, note = "" } = {}) => {
      const res = await api.post("/cart/items", { product_id, quantity, note });
      setCart(res.data || { items: [] });
      return res.data;
    },
    []
  );

  const updateItem = useCallback(async (itemId, patch) => {
    const res = await api.patch(`/cart/items/${itemId}`, patch);
    setCart(res.data || { items: [] });
  }, []);

  const removeItem = useCallback(async (itemId) => {
    const res = await api.delete(`/cart/items/${itemId}`);
    setCart(res.data || { items: [] });
  }, []);

  const clear = useCallback(async () => {
    const res = await api.delete("/cart");
    setCart(res.data || { items: [] });
  }, []);

  const submit = useCallback(async (customer_message = "") => {
    const res = await api.post("/cart/submit", { customer_message });
    await refresh();
    return res.data;
  }, [refresh]);

  const itemCount = (cart.items || []).reduce(
    (sum, it) => sum + (it.quantity || 0),
    0
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        itemCount,
        drawerOpen,
        setDrawerOpen,
        refresh,
        addItem,
        updateItem,
        removeItem,
        clear,
        submit,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside a CartProvider");
  return ctx;
}
